import React from 'react';
import { Link } from 'react-router-dom';
import logo from './rounded_logo.png';
import welcomeScreenEn from './assets/mockups/welcomeScreen.png';
import medicationScreenEn from './assets/mockups/medicationScreen.png';
import scheduleScreenEn from './assets/mockups/scheduleScreen.png';
import scannerScreenEn from './assets/mockups/scannerScreen.png';
import wellBeingScreenEn from './assets/mockups/wellBeingScreen.png';
import securityScreenEn from './assets/mockups/securityScreen.png';
import welcomeScreenUk from './assets/mockups/ua/welcomeScreen_ua.png';
import medicationScreenUk from './assets/mockups/ua/medicationScreen_ua.png';
import scheduleScreenUk from './assets/mockups/ua/scheduleScreen_ua.png';
import scannerScreenUk from './assets/mockups/ua/scannerScreen_ua.png';
import wellBeingScreenUk from './assets/mockups/ua/wellBeingScreen_ua.png';
import securityScreenUk from './assets/mockups/ua/securityScreen_ua.png';
import appStoreBadge from './assets/logos/appstore.png';
import { type Language } from './utils/languageUtils';

interface HomeProps {
  language: Language;
  toggleLanguage: () => void;
  translations: any;
}

const screenshots = {
  en: {
    welcomeScreen: welcomeScreenEn,
    medicationScreen: medicationScreenEn,
    scheduleScreen: scheduleScreenEn,
    scannerScreen: scannerScreenEn,
    wellBeingScreen: wellBeingScreenEn,
    securityScreen: securityScreenEn,
  },
  uk: {
    welcomeScreen: welcomeScreenUk,
    medicationScreen: medicationScreenUk,
    scheduleScreen: scheduleScreenUk,
    scannerScreen: scannerScreenUk,
    wellBeingScreen: wellBeingScreenUk,
    securityScreen: securityScreenUk,
  },
};

function Home({ language, toggleLanguage, translations }: HomeProps) {
  const t = translations[language];
  const images = screenshots[language];
  const APP_STORE_LINK = 'https://apps.apple.com/app/medisnap';

  return (
    <>
      {/* Language Toggle */}
      <button className="language-toggle" onClick={toggleLanguage} aria-label="Toggle language">
        {language === 'en' ? '🇺🇦 UA' : '🇬🇧 EN'}
      </button>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <img src={logo} alt="MediSnap Logo" className="logo" />
          <h1>{t.hero.title}</h1>
          <p className="subtitle">{t.hero.subtitle}</p>

          <div className="download-buttons">
            <a
              href={APP_STORE_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="store-button"
            >
              <img src={appStoreBadge} alt="Download on the App Store" className="store-badge" />
            </a>
          </div>
        </div>

        <div className="mockup-hero">
          <img src={images.welcomeScreen} alt="MediSnap Welcome Screen" className="phone-mockup" />
        </div>

        <div
          className="scroll-indicator"
          onClick={() => {
            document.querySelector('.features')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 5V19M12 19L19 12M12 19L5 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
      </section>

      {/* Features Section */}
      <section className="features">
        <h2>{t.features.title}</h2>
        <div className="features-grid">
          {t.features.items.map((feature: any, index: number) => (
            <div key={index} className="feature-card">
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How It Works Section */}
      <section className="how-it-works">
        <h2>{t.howItWorks.title}</h2>
        <div className="steps">
          <div className="step">
            <div className="step-number">1</div>
            <img src={images.scannerScreen} alt="Scan prescription" className="step-image" />
            <h3>{t.howItWorks.steps[0].title}</h3>
            <p>{t.howItWorks.steps[0].description}</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <img src={images.medicationScreen} alt="Review medications" className="step-image" />
            <h3>{t.howItWorks.steps[1].title}</h3>
            <p>{t.howItWorks.steps[1].description}</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <img src={images.scheduleScreen} alt="Get reminders" className="step-image" />
            <h3>{t.howItWorks.steps[2].title}</h3>
            <p>{t.howItWorks.steps[2].description}</p>
          </div>
        </div>
      </section>

      {/* Additional Screenshots */}
      <section className="screenshots">
        <div className="screenshots-grid">
          <img src={images.wellBeingScreen} alt="Well-being tracking" className="screenshot" />
          <img src={images.securityScreen} alt="Security settings" className="screenshot" />
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <p>{t.footer.madeWith}</p>
        <p className="app-name">MediSnap © 2025</p>
        <Link to="/privacy" className="privacy-link">
          {language === 'en' ? 'Privacy Policy' : 'Політика конфіденційності'}
        </Link>
      </footer>
    </>
  );
}

export default Home;
